import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import FeatureGuard from "./admin/components/FeatureGuard";
import ProtectedAdminRoute from "./admin/components/ProtectedAdminRoute";

import AdminActivityPage from "./admin/pages/AdminActivityPage";
import AdminCouponsPage from "./admin/pages/AdminCouponsPage";
import AdminCustomerDetailPage from "./admin/pages/AdminCustomerDetailPage";
import AdminCustomersPage from "./admin/pages/AdminCustomersPage";
import AdminDashboardPage from "./admin/pages/AdminDashboardPage";
import AdminDeliveryPage from "./admin/pages/AdminDeliveryPage";
import AdminInventoryPage from "./admin/pages/AdminInventoryPage";
import AdminLoginPage from "./admin/pages/AdminLoginPage";
import AdminMenuPage from "./admin/pages/AdminMenuPage";
import AdminMessageDetailPage from "./admin/pages/AdminMessageDetailPage";
import AdminMessagesPage from "./admin/pages/AdminMessagesPage";
import AdminMonthlySpecialsPage from "./admin/pages/AdminMonthlySpecialsPage";
import AdminOrderDetailPage from "./admin/pages/AdminOrderDetailPage";
import AdminReportsPage from "./admin/pages/AdminReportsPage";
import ResetPasswordPage from "./admin/pages/ResetPasswordPage";
import AdminSettingsPage from "./admin/pages/AdminSettingsPage";
import AdminOrdersPage from "./admin/pages/AdminOrdersPage";
import LandingPage from "./Public routes/home/LandingPage";
import MenuPage from "./Public routes/storefront/menu/MenuPage";
import CartPage from "./Public routes/storefront/cart/CartPage";
import CheckoutPage from "./Public routes/storefront/checkout/CheckoutPage";
import AboutPage from "./Public routes/about/AboutPage";
import DeliveryPage from "./Public routes/delivery/DeliveryPage";
import ContactPage from "./Public routes/contact/ContactPage";
import PublicFooter from "./Public routes/components/PublicFooter";
import PublicSEO from "./Public routes/components/PublicSEO";
import LegalPage from "./Public routes/legal/LegalPage";
import PaymentSuccessPage from "./Public routes/system/PaymentSuccessPage";
import PaymentFailurePage from "./Public routes/system/PaymentFailurePage";
import NotFoundPage from "./Public routes/system/NotFoundPage";
import ServerErrorPage from "./Public routes/system/ServerErrorPage";
import PublicErrorBoundary from "./Public routes/system/PublicErrorBoundary";

import "./styles/global.css";
import "./styles/admin.css";


const seo = {
  home: {
    title: "SugaLumps Cupcakery & Sweet Treats | Silver Spring, MD",
    description: "Delivery-only cupcakes, pie pockets, cheesecakes, pies, and handcrafted sweet treats made to order in Silver Spring for the DMV.",
    canonicalPath: "/",
    structuredData: "business",
  },
  menu: {
    title: "Dessert Menu | SugaLumps Cupcakery & Sweet Treats",
    description: "Explore SugaLumps cupcakes, pie pockets, pies, cheesecakes, flavors, sizes, and made-to-order dessert options for delivery.",
    canonicalPath: "/menu",
  },
  about: {
    title: "About SugaLumps | Black Woman-Owned Bakery in Silver Spring",
    description: "Meet SugaLumps Cupcakery & Sweet Treats, a Black woman-owned bakery creating made-to-order desserts for celebrations and cravings.",
    canonicalPath: "/about",
  },
  delivery: {
    title: "Dessert Delivery in the DMV | SugaLumps",
    description: "Learn how SugaLumps delivery works, including address-based eligibility, free delivery range, paid delivery areas, and scheduling.",
    canonicalPath: "/delivery",
  },
  contact: {
    title: "Contact SugaLumps Cupcakery & Sweet Treats",
    description: "Contact SugaLumps for delivery questions, custom dessert inquiries, order help, flavors, coupons, and website support.",
    canonicalPath: "/contact",
  },
  terms: {
    title: "Terms of Service | SugaLumps",
    description: "Review SugaLumps ordering, delivery, payment, promotions, custom dessert, cancellation, and website-use terms.",
    canonicalPath: "/terms",
  },
  privacy: {
    title: "Privacy Policy | SugaLumps",
    description: "Learn how SugaLumps handles order, delivery, payment-status, contact, marketing, routing, and browser-storage information.",
    canonicalPath: "/privacy",
  },
  cart: {
    title: "Cart | SugaLumps",
    description: "Review your SugaLumps cart before checkout.",
    canonicalPath: "/cart",
    robots: "noindex,nofollow",
  },
  checkout: {
    title: "Checkout | SugaLumps",
    description: "Secure SugaLumps checkout.",
    canonicalPath: "/checkout",
    robots: "noindex,nofollow",
  },
  success: {
    title: "Payment Success | SugaLumps",
    description: "SugaLumps payment confirmation.",
    canonicalPath: "/payment/success",
    robots: "noindex,nofollow",
  },
  failed: {
    title: "Payment Failed | SugaLumps",
    description: "SugaLumps payment issue page.",
    canonicalPath: "/payment/failed",
    robots: "noindex,nofollow",
  },
  serverError: {
    title: "Server Error | SugaLumps",
    description: "SugaLumps server error page.",
    canonicalPath: "/500",
    robots: "noindex,nofollow",
  },
  notFound: {
    title: "Page Not Found | SugaLumps",
    description: "SugaLumps page not found.",
    canonicalPath: "/404",
    robots: "noindex,nofollow",
  },
  resetPassword: {
    title: "Reset Password | SugaLumps Admin",
    description: "Reset your SugaLumps admin password.",
    canonicalPath: "/reset-password",
    robots: "noindex,nofollow",
  },
  admin: {
    title: "SugaLumps Admin",
    description: "SugaLumps private admin area.",
    canonicalPath: "/login",
    robots: "noindex,nofollow",
  },
};

function ProtectedPage({ children }) {
  return <><PublicSEO {...seo.admin} /><ProtectedAdminRoute>{children}</ProtectedAdminRoute></>;
}

function PublicPage({ children, meta }) {
  return (
    <>
      <PublicSEO {...meta} />
      <PublicErrorBoundary>{children}</PublicErrorBoundary>
      <PublicFooter />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicPage meta={seo.home}><LandingPage /></PublicPage>} />
        <Route path="/menu" element={<PublicPage meta={seo.menu}><MenuPage /></PublicPage>} />
        <Route path="/cart" element={<PublicPage meta={seo.cart}><CartPage /></PublicPage>} />
        <Route path="/checkout" element={<PublicPage meta={seo.checkout}><CheckoutPage /></PublicPage>} />
        <Route path="/login" element={<><PublicSEO {...seo.admin} /><AdminLoginPage /></>} />
        <Route path="/reset-password/:uid/:token" element={<><PublicSEO {...seo.resetPassword} /><ResetPasswordPage /></>} />
        <Route path="/about" element={<PublicPage meta={seo.about}><AboutPage /></PublicPage>} />
        <Route path="/contact" element={<PublicPage meta={seo.contact}><ContactPage /></PublicPage>} />
        <Route path="/delivery" element={<PublicPage meta={seo.delivery}><DeliveryPage /></PublicPage>} />
        <Route path="/terms" element={<PublicPage meta={seo.terms}><LegalPage type="terms" /></PublicPage>} />
        <Route path="/privacy" element={<PublicPage meta={seo.privacy}><LegalPage type="privacy" /></PublicPage>} />
        <Route path="/admin" element={<ProtectedPage><AdminDashboardPage /></ProtectedPage>} />
        <Route path="/admin/orders" element={<ProtectedPage><AdminOrdersPage /></ProtectedPage>} />
        <Route path="/admin/orders/:orderId" element={<ProtectedPage><AdminOrderDetailPage /></ProtectedPage>} />
        <Route path="/admin/menu" element={<ProtectedPage><AdminMenuPage /></ProtectedPage>} />
        <Route path="/admin/inventory" element={<ProtectedPage><FeatureGuard feature="inventory_enabled"><AdminInventoryPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/deliveries" element={<ProtectedPage><AdminDeliveryPage /></ProtectedPage>} />
        <Route path="/admin/specials" element={<ProtectedPage><FeatureGuard feature="monthly_specials_enabled"><AdminMonthlySpecialsPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/coupons" element={<ProtectedPage><FeatureGuard feature="coupons_enabled"><AdminCouponsPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/reports" element={<ProtectedPage><FeatureGuard feature="reports_enabled"><AdminReportsPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/activity" element={<ProtectedPage><AdminActivityPage /></ProtectedPage>} />
        <Route path="/admin/settings" element={<ProtectedPage><AdminSettingsPage /></ProtectedPage>} />
        <Route path="/admin/customers" element={<ProtectedPage><FeatureGuard feature="customer_crm_enabled"><AdminCustomersPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/customers/:customerId" element={<ProtectedPage><FeatureGuard feature="customer_crm_enabled"><AdminCustomerDetailPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/messages" element={<ProtectedPage><FeatureGuard feature="messages_enabled"><AdminMessagesPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/messages/:conversationId" element={<ProtectedPage><FeatureGuard feature="messages_enabled"><AdminMessageDetailPage /></FeatureGuard></ProtectedPage>} />
        <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
        <Route path="/payment/success" element={<PublicPage meta={seo.success}><PaymentSuccessPage /></PublicPage>} />
        <Route path="/payment/failed" element={<PublicPage meta={seo.failed}><PaymentFailurePage /></PublicPage>} />
        <Route path="/500" element={<PublicPage meta={seo.serverError}><ServerErrorPage /></PublicPage>} />
        <Route path="*" element={<PublicPage meta={seo.notFound}><NotFoundPage /></PublicPage>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
